import http from '@/utils/http'
import type { AxiosRequestConfig } from 'axios'

/**
 * API响应基础接口
 */
export interface ApiResponse<T = any> {
  success: boolean
  data: T
  message?: string
  errors?: string[]
}

/**
 * 分页请求基础接口
 */
export interface PagedRequest {
  pageSize?: number
  pageIndex?: number
  sorting?: string
}

/**
 * 分页响应基础接口
 */
export interface PagedResponse<T = any> {
  items: T[]
  totalCount: number
}

/**
 * 服务基类
 * 提供通用的服务功能，不直接包含HTTP实现，而是使用HTTP工具类
 */
export abstract class BaseService {
  protected abstract readonly baseUrl: string

  /**
   * GET请求
   */
  protected async get<T = any>(
    endpoint: string = '',
    params?: Record<string, any>,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const url = this.buildUrl(endpoint)
      const requestConfig = { ...config, params }
      const response = await http.get<T>(url, requestConfig)
      return this.handleResponse(response)
    } catch (error) {
      throw this.handleError(error, 'GET', endpoint)
    }
  }

  /**
   * POST请求
   */
  protected async post<T = any>(
    endpoint: string = '',
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const url = this.buildUrl(endpoint)
      const response = await http.post<T>(url, data, config)
      return this.handleResponse(response)
    } catch (error) {
      throw this.handleError(error, 'POST', endpoint)
    }
  }

  /**
   * PUT请求
   */
  protected async put<T = any>(
    endpoint: string = '',
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const url = this.buildUrl(endpoint)
      const response = await http.put<T>(url, data, config)
      return this.handleResponse(response)
    } catch (error) {
      throw this.handleError(error, 'PUT', endpoint)
    }
  }

  /**
   * DELETE请求
   */
  protected async delete<T = any>(
    endpoint: string = '',
    params?: Record<string, any>,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const url = this.buildUrl(endpoint)
      const requestConfig = { ...config, params }
      const response = await http.delete<T>(url, requestConfig)
      return this.handleResponse(response)
    } catch (error) {
      throw this.handleError(error, 'DELETE', endpoint)
    }
  }

  /**
   * 构建完整URL
   */
  private buildUrl(endpoint: string): string {
    const baseUrl = this.baseUrl.startsWith('/') ? this.baseUrl : `/${this.baseUrl}`
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint
    
    return endpoint 
      ? `${baseUrl}/${cleanEndpoint}` 
      : baseUrl
  }

  /**
   * 处理响应数据
   * 从axios response中提取data
   */
  private handleResponse<T>(response: any): T {
    return response.data
  }

  /**
   * 处理错误
   */
  private handleError(error: any, method: string, endpoint: string): Error {
    const errorMessage = this.extractErrorMessage(error)
    const fullErrorMessage = `${method} ${this.buildUrl(endpoint)} failed: ${errorMessage}`
    
    console.error(fullErrorMessage, error)
    
    return new Error(errorMessage)
  }

  /**
   * 提取错误信息
   */
  private extractErrorMessage(error: any): string {
    const responseData = error?.response?.data

    if (responseData) {
      if (typeof responseData === 'string') {
        return responseData
      }

      const abpError = responseData.error
      if (abpError) {
        if (abpError.message) {
          return abpError.message
        }

        if (abpError.details) {
          return abpError.details
        }

        if (Array.isArray(abpError.validationErrors) && abpError.validationErrors.length > 0) {
          const validationMessages = abpError.validationErrors
            .map((item: any) => item.message || item.detail)
            .filter(Boolean)

          if (validationMessages.length > 0) {
            return validationMessages.join('; ')
          }
        }
      }

      if (responseData.message) {
        return responseData.message
      }

      if (Array.isArray(responseData.errors) && responseData.errors.length > 0) {
        return responseData.errors.join('; ')
      }

      if (responseData.detail) {
        return responseData.detail
      }
    }

    if (error?.message) {
      return error.message
    }
    
    if (error?.response?.statusText) {
      return error.response.statusText
    }
    
    return '请求失败，请稍后重试'
  }

  /**
   * 日志记录
   */
  protected log(message: string, data?: any): void {
    if (import.meta.env.DEV) {
      console.log(`[${this.constructor.name}] ${message}`, data)
    }
  }

  /**
   * 错误日志记录
   */
  protected logError(message: string, error?: any): void {
    console.error(`[${this.constructor.name}] ${message}`, error)
  }
}